import { useEffect, useMemo, useState } from "react";
import { adminFetch } from "./adminApi";

function Chip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/80">
      {children}
    </span>
  );
}

const STATUS = ["pending", "accepted", "declined", "completed", "cancelled"];
const PAY = ["unpaid", "paid"];

export default function AdminBookings() {
  const [status, setStatus] = useState("ALL");
  const [tutorId, setTutorId] = useState("");
  const [studentId, setStudentId] = useState("");
  const [page, setPage] = useState(1);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // selected + force panel
  const [selected, setSelected] = useState(null);
  const [forceStatus, setForceStatus] = useState("pending");
  const [forcePay, setForcePay] = useState("unpaid");
  const [forceReason, setForceReason] = useState("");
  const [forceBusy, setForceBusy] = useState(false);

  const pageSize = 20;

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("pageSize", String(pageSize));
    if (status !== "ALL") p.set("status", status);
    if (tutorId.trim()) p.set("tutorId", tutorId.trim());
    if (studentId.trim()) p.set("studentId", studentId.trim());
    return p.toString();
  }, [page, status, tutorId, studentId]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await adminFetch(`/api/admin/bookings?${query}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
    } catch (e) {
      setErr(e?.message || "Failed to load bookings");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function selectBooking(b) {
    setSelected(b);
    setForceStatus(STATUS.includes(b.status) ? b.status : "pending");
    setForcePay(PAY.includes(b.paymentStatus) ? b.paymentStatus : "unpaid");
    setForceReason("");
  }

  async function applyForce() {
    if (!selected?.id) return;
    const reason = String(forceReason || "").trim();
    if (!reason) return alert("Reason is required");

    setForceBusy(true);
    try {
      await adminFetch(`/api/admin/bookings/${selected.id}`, {
        method: "PATCH",
        body: { status: forceStatus, paymentStatus: forcePay, reason },
      });

      await load();
      // обновим selected из свежего списка
      const fresh = items.find((x) => x.id === selected.id);
      if (fresh) selectBooking(fresh);
    } catch (e) {
      alert(e?.data?.error || e?.message || "Failed to update booking");
    } finally {
      setForceBusy(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const canPrev = page > 1;
  const canNext = page * pageSize < total;

  return (
    <div className="text-white">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="font-unbounded text-2xl font-bold">Bookings</h2>
          <p className="mt-2 text-white/70">Filters: status, tutorId, studentId.</p>
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          >
            <option value="ALL">All statuses</option>
            {STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <input
            value={tutorId}
            onChange={(e) => {
              setTutorId(e.target.value);
              setPage(1);
            }}
            placeholder="tutorId…"
            className="w-full md:w-44 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />

          <input
            value={studentId}
            onChange={(e) => {
              setStudentId(e.target.value);
              setPage(1);
            }}
            placeholder="studentId…"
            className="w-full md:w-44 rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
          />

          <button
            onClick={load}
            className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        {/* LIST */}
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm text-white/70">{loading ? "Loading…" : `Total: ${total}`}</div>
            <div className="flex items-center gap-2">
              <button
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
              >
                Prev
              </button>
              <Chip>Page {page}</Chip>
              <button
                disabled={!canNext}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-white/15"
              >
                Next
              </button>
            </div>
          </div>

          {err && <div className="px-4 py-3 text-sm text-red-300">{err}</div>}

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-white/60">
                <tr className="border-b border-white/10">
                  <th className="px-4 py-3 text-left">When</th>
                  <th className="px-4 py-3 text-left">Subject</th>
                  <th className="px-4 py-3 text-left">Tutor</th>
                  <th className="px-4 py-3 text-left">Student</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Payment</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-white/60">
                      No bookings found
                    </td>
                  </tr>
                ) : (
                  items.map((b) => (
                    <tr key={b.id} className="border-b border-white/10">
                      <td className="px-4 py-3 text-white/80">
                        <div className="font-medium">{new Date(b.startISO).toLocaleString()}</div>
                        <div className="text-xs text-white/50">{b.id}</div>
                      </td>
                      <td className="px-4 py-3 text-white/80">{b.subject}</td>
                      <td className="px-4 py-3 text-white/70">{b.tutorId}</td>
                      <td className="px-4 py-3 text-white/70">{b.studentId}</td>
                      <td className="px-4 py-3">
                        <Chip>{b.status}</Chip>
                      </td>
                      <td className="px-4 py-3">
                        <Chip>{b.paymentStatus}</Chip>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => selectBooking(b)}
                          className="rounded-lg bg-white/10 px-3 py-1.5 text-xs hover:bg-white/15 ring-1 ring-white/10"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* FORCE PANEL */}
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
          <h3 className="font-unbounded text-lg font-bold">Force update</h3>

          {!selected ? (
            <div className="mt-3 text-sm text-white/70">Click “Edit” on a booking.</div>
          ) : (
            <div className="mt-3 space-y-3 text-sm">
              <div className="text-xs text-white/50 break-all">{selected.id}</div>

              <div className="grid gap-2">
                <div>
                  <div className="text-xs text-white/50">Status</div>
                  <select
                    value={forceStatus}
                    onChange={(e) => setForceStatus(e.target.value)}
                    className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
                  >
                    {STATUS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs text-white/50">Payment</div>
                  <select
                    value={forcePay}
                    onChange={(e) => setForcePay(e.target.value)}
                    className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
                  >
                    {PAY.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="text-xs text-white/50">Reason (required)</div>
                  <input
                    value={forceReason}
                    onChange={(e) => setForceReason(e.target.value)}
                    placeholder="Why are you changing this?"
                    className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm outline-none ring-1 ring-white/10 focus:ring-white/30"
                  />
                </div>

                <button
                  disabled={forceBusy}
                  onClick={applyForce}
                  className="rounded-lg bg-white/10 px-3 py-2 text-sm hover:bg-white/15 ring-1 ring-white/10 disabled:opacity-50"
                >
                  {forceBusy ? "Applying…" : "Apply"}
                </button>

                <div className="text-xs text-white/50">Writes to AdminAuditLog (BOOKING_FORCE_UPDATE).</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
